import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { Account } from "../../models/Account";
import api from "../../api/axios";
import { fetchAccounts } from "../../redux/slices/accountsSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchResellers } from "../../redux/slices/resellerSlice";

interface ResellerFormModalProps {
  visible: boolean;
  onClose: () => void;
  editingReseller: any | null;
}

export function ResellerFormModal({
  visible,
  onClose,
  editingReseller,
}: ResellerFormModalProps) {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { accounts } = useSelector((state: RootState) => state.accounts);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingReseller) {
      form.setFieldsValue(editingReseller);
    } else {
      form.resetFields();
      form.setFieldsValue({
        isEsimReseller: true,
      });
    }
  }, [editingReseller, form]);

  useEffect(() => {
    dispatch(fetchAccounts());
  }, [dispatch]);

  const handleFinish = async (values: any) => {
    setSaving(true);
    try {
      const fullPayload = {
        ...editingReseller, 
        ...values,
        isAdmin: values.isAdmin ?? editingReseller?.isAdmin ?? false,
        resellerId: values.resellerId ?? editingReseller?.resellerId ?? null,
        stockAccountId:
          values.stockAccountId ?? editingReseller?.stockAccountId ?? null,
        salesAccountId:
          values.salesAccountId ?? editingReseller?.salesAccountId ?? null,
      };
      await api.post("/User/register", fullPayload);
      message.success("Reseller saved successfully");
      await dispatch(fetchResellers());
      onClose();
    } catch (error) {
      message.error("Failed to save reseller");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={editingReseller ? "Edit Reseller" : "Create Reseller"}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
      confirmLoading={saving}
      okButtonProps={{ disabled: saving }}
      cancelButtonProps={{ disabled: saving }}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: !editingReseller, min: 6 }]}
              >
                <Input.Password />
              </Form.Item>
            </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: "email" }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item name="stockAccountId" label="Stock Account">
              <Select allowClear>
                {accounts.map((acc) => (
                  <Select.Option key={acc.id} value={acc.id}>
                    {acc.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="balance"
              label="Balance"
              rules={[{ required: true, message: "Please input balance" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="balancePassword"
              label="Balance Password"
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="salesAccountId" label="Sales Account">
              <Select allowClear>
                {accounts.map((acc) => (
                  <Select.Option key={acc.id} value={acc.id}>
                    {acc.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col
            xs={24}
            sm={12}
            style={{ display: "flex",flexDirection:"column" }}
          >
            <Form.Item name="isAdmin" valuePropName="checked" style={{margin:0}}>
              <Checkbox>Is Admin</Checkbox>
            </Form.Item>
            <Form.Item name="isEsimReseller" valuePropName="checked" style={{margin:0}}>
              <Checkbox>Is Esim Reseller</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
